/**
 *
 * AttendanceControl
 *
 */

import React, { memo, useState, useEffect, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import { compose } from 'redux';
import classNames from 'classnames';
import { useTranslation } from "react-i18next";

import { useInjectSaga } from 'utils/injectSaga';
import { useInjectReducer } from 'utils/injectReducer';
import reducer from './reducer';
import saga from './saga';

import useDeviceDetect from 'utils/useDeviceDetect';

// styles
import './style.scss';

import {
  flushStateAttendanceControl,
  loadAttendance,
  changeAttendance,
  loadCourse,
} from "./actions";
import {
  makeSelectAttendanceData,
  selectAttendanceLoading,
  selectAttendanceError,
  selectAttendanceLoadingChange,
  // course
  makeSelectCourseDetail,
  makeSelectCourseLoad,
  makeSelectCourseError,
  // on date
  makeSelectOnDateAbsences,
  makeSelectOnDateAttendances,
} from "./selectors";

// layout components
import Preloader from "components/Preloader";
import ErrorBoundary from "engine/ErrorBoundary";

// antd component
import { Button, Modal } from "antd";

// assets
import {
  ArrowLeftOutlined,
  CloseOutlined,
} from "@ant-design/icons";

// components
import StepByStepWrapper from "mobile/components/StepByStepWrapper";
import ExcusedStudents from "mobile/components/ExcusedStudents";
// cta notifications
import FinalControlScreen from "mobile/components/FinalControlScreen";
// inner components
import {
  ModalChangePresence,
  PassControlSelection,
  ControlStdPresens,
  ResumeControlPresens,
} from "./components";

// utils
import { useRefState } from "utils/hooks";
import {
  clean,
  getUniqueArrayObj,
  getObjId,
} from "utils/helpers/helper.general";
import {
  isSameDates,
  convertDateToString,
} from "utils/moment/moment.utils";
import {
  getExcusedAbsencesByStudent,
  getExcusedStudents,
} from "utils/helpers/helper.generateData";
import _local from "utils/localStorageUtils";

export function AttendanceControl({
  // main
  courseID,
  // props
  onDate,
  timeZone,
  updateCourse,
  updateAttendances,
  // states
  courseDetail,
  onDateAbsences,
  onDateAttendances,
  courseLoading,
  attendanceData,
  loading,
  loadingCnahge,
  // default states
  dateFormat,
  history,
  // actions
  flushState,
  loadCourse,
  loadAttendance,
  changeAttendance,
  // default
  mode,
  className,
}) {
  useInjectReducer({ key: "attendanceControl", reducer });
  useInjectSaga({ key: "attendanceControl", saga });

  const { t, i18n } = useTranslation();

  const childClassNames = classNames("attendance-control-container", className);
  const { isMobile } = useDeviceDetect();

  const localUser = _local.getLocalUser();

  const [controlType, setControlType] = useState("all"); // all, check-absent, check-present

  const [isOpenAlert, setIsOpenAlert] = useState(false);
  const [isOpenModal, setIsOpenModal] = useState(false);

  const [step, setStep] = useState(1);
  const [totalStep, setTotalStep] = useState(3);
  const [showFinal, setShowFinal] = useState(false);

  const initAttendance = {
    _id: null,
    course: courseDetail?._id || null,
    date: onDate,
    timeZone: !!timeZone && timeZone,
    absences: [],
    missing_students: [],
    students: [],
  };

  const [currentCourseID, setCurrentCourseID] = useState(false);
  const [
    currentAttendance,
    currentAttendanceRef,
    setCurrentAttendance,
  ] = useRefState(initAttendance);

  /**
   * Loading Course data for rendering in a component
   * - work like ComponentDidUpdate
   */
  useEffect(() => {
    // Clearing the state after unmounting a component
    // Anything in here is fired on component unmount.
    // !!courseID && loadCourse(courseID);
    setCurrentCourseID(courseID);
    return () => {
      flushState();
    };
  }, []);

  useEffect(() => {
    if (!!courseID && courseID != currentCourseID) {
      setCurrentCourseID(courseID);
    }
  }, [courseID]);

  /**
   * Loading data for rendering in a component
   * - work like ComponentDidUpdate
   */
  useEffect(() => {
    if (courseDetail?._id) {
      updateCourse(courseDetail);
      const currentAttendanceArr = onDateAttendances?.filter(att =>
        !!att && isSameDates(onDate, att?.date) && getObjId(att?.course) == getObjId(courseDetail)
      );

      const originalAbs = onDateAbsences?.filter(a => a?.status != "refused") || [];

      const newData = {
        course: courseDetail?._id,
        absences: getExcusedAbsencesByStudent(
          courseDetail?.students,
          onDateAbsences,
        ),
        missing_students: getExcusedStudents(
          courseDetail?.students,
          originalAbs,
          onDateAttendances,
        ),
      };
  
      if (currentAttendanceArr?.length > 0) {
        setCurrentAttendance(currentAttendanceArr?.[0]);
        openAlert();
      } else {
        setCurrentAttendance({
          ...initAttendance,
          ...newData,
        });
        openAttendanceModal();
      }
    } else {
      setCurrentAttendance(initAttendance);
    }
  }, [courseDetail, onDate]);

  // Refresh current course attendances list
  useEffect(() => {
    if (!loadingCnahge && attendanceData) {
      if (isSameDates(attendanceData?.date, currentAttendance?.date)) {
        setCurrentAttendance(attendanceData);
      }

      // refresh current course attendances list
      if (onDateAttendances?.length > 0) {
        let attend = onDateAttendances || [];
        if (onDateAttendances?.length > 0) {
          attend = onDateAttendances?.map(att => {
            if (
              isSameDates(attendanceData?.date, att?.date) &&
              getObjId(att?.course) == getObjId(courseDetail)
            ) {
              return { ...att, ...attendanceData };
            } else {
              return att;
            }
          });
        } else {
          attend = getUniqueArrayObj([...attend, attendanceData], "_id");
        }

        updateCourse({
          ...courseDetail,
          attendances: attend,
        });
      } else {
        updateCourse({
          ...courseDetail,
          attendances: [attendanceData],
        });
      }
    }
  }, [attendanceData]);

  const closeModal = useCallback(() => {
    setShowFinal(false);
    setIsOpenModal(false);
    setControlType("all");
    // ----
    setStep(1);
    setCurrentAttendance(initAttendance);
    if (mode == "attendance") {
      updateCourse(false);
    }
  }, [initAttendance]);
  const openAlert = useCallback(() => {
    setIsOpenAlert(true);
    setIsOpenModal(false);
  }, []);
  const openAttendanceModal = useCallback(() => {
    setIsOpenAlert(false);
    setIsOpenModal(true);
  }, []);
  const alertOk = useCallback(() => {
    openAttendanceModal();
  }, []);
  const alertCancel = useCallback(() => {
    setIsOpenAlert(false);
    setCurrentAttendance(initAttendance);
    setShowFinal(false);
    setIsOpenModal(false);
    setControlType("all");
    // ----
    setStep(1);
    if (mode == "attendance") {
      updateCourse(false);
    }
  }, [initAttendance]);

  const openModalCallback = useCallback(() => {
    setStep(1);
    setShowFinal(false);
    !!courseID && loadCourse(courseID, onDate, timeZone);
  }, [currentAttendance, courseID, courseLoading, onDate, timeZone]);

  // default form options
  let formOptions = {
    formData: currentAttendanceRef?.current || initAttendance,
    course: courseDetail,
    absences: onDateAbsences,
    attendances: onDateAttendances,
    currentStep: step,
    // Step Navigation
    nextStep: () => step != totalStep && setStep(step + 1),
    prevStep: () => {
      return step > 1 && setStep(step - 1);
    },
    goToStep: number => setStep(number),
    // other
    controlType: controlType,
    changeControlType: type => setControlType(type),
    result: values => resultData(values),
  };

  // Main result/save data
  const resultData = useCallback(
    result => {
      const newObj = { ...currentAttendanceRef?.current, ...result };
      setCurrentAttendance(newObj);
      formOptions.formData = newObj;
      if (localUser?._id && step == totalStep) {
        setShowFinal(true);
        changeAttendance(clean(newObj), timeZone);
        updateAttendances(clean(newObj));
      }
    },
    [currentAttendanceRef, localUser, step, totalStep, formOptions, timeZone],
  );

  const finalNotification = useMemo(() => {
    return (
      <FinalControlScreen loading={loadingCnahge} continueAction={closeModal} />
    );
  }, [loadingCnahge, closeModal, i18n?.language]);

  const controlSteps = useCallback(
    stepNumber => {
      const courseTitle = `${courseDetail?.subject?.name}${
        courseDetail?.course_code ? " (" + courseDetail?.course_code + ")" : ""
      }${onDate ? " - " + convertDateToString(onDate) : ""}`;
      switch (stepNumber) {
        case 1:
          return (
            <>
              {!showFinal && (
                <StepByStepWrapper
                  number={1}
                  isActive={step == 1}
                  header={t("attendances.attendanceContorlTitle")}
                  title={courseTitle}
                >
                  <PassControlSelection {...formOptions} />
                  <ExcusedStudents
                    course={courseDetail}
                    dataSource={courseDetail?.students || []}
                    onDate={onDate}
                    absences={onDateAbsences}
                    attendances={onDateAttendances}
                  />
                </StepByStepWrapper>
              )}
              {/* // Last Step Notificationswindows */}
              {showFinal && finalNotification}
            </>
          );
        case 2:
          return (
            <>
              {!showFinal && (
                <StepByStepWrapper
                  number={2}
                  isActive={step == 2}
                  header={t("attendances.attendanceContorlTitle")}
                  title={courseTitle}
                >
                  <ControlStdPresens {...formOptions} />
                </StepByStepWrapper>
              )}
              {/* // Last Step Notificationswindows */}
              {showFinal && finalNotification}
            </>
          );
        case 3:
          return (
            <>
              {!showFinal && (
                <StepByStepWrapper
                  number={3}
                  isActive={step == 3}
                  header={t("attendances.attendanceContorlTitle")}
                  title={t("absences.listOfAbsents")} 
                >
                  <ResumeControlPresens
                    {...formOptions}
                    formData={currentAttendance}
                  />
                </StepByStepWrapper>
              )}
              {/* // Last Step Notificationswindows */}
              {showFinal && finalNotification}
            </>
          );
      }
    },
    [
      showFinal,
      formOptions,
      step,
      courseDetail,
      onDate,
      onDateAbsences,
      onDateAttendances,
      currentAttendance,
      currentAttendanceRef,
    ],
  );

  const renderModal = useMemo(() => {
    return (
      courseDetail?._id && (
        <Modal
          className="attendance-control"
          title={
            <>
              {isMobile && (
                <>
                  {/* <MoreOutlined className="attendance-control__mobile-menu" /> */}
                  <div className="attendance-control__mobile-menu" />
                </>
              )}
              {t("attendances.attendanceContorlTitle")}
            </>
          }
          open={isOpenModal}
          closeIcon={
            <span className="attendance-control__close" onClick={closeModal}>
              {!isMobile ? (
                <>
                  <CloseOutlined /> {t("common.close")}
                </>
              ) : (
                <ArrowLeftOutlined />
              )}
            </span>
          }
          onCancel={closeModal}
          footer={[]}
        >
          {!!courseLoading && <Preloader />}
          {!courseLoading && controlSteps(step)}
        </Modal>
      )
    );
  }, [
    step,
    isMobile,
    isOpenModal,
    closeModal,
    courseLoading,
    courseDetail,
    formOptions,
    currentAttendanceRef,
    i18n?.language
  ]);

  const renderBtn = useMemo(
    () => (
      <Button
        className={`ant-btn-submit ${!!!currentCourseID ? "disabled" : ""}`}
        disabled={!!!currentCourseID}
        loading={courseLoading}
        onClick={openModalCallback}
      >
        {t("attendances.attendanceContorlTitle")}
      </Button>
    ),
    [courseDetail, openModalCallback, courseLoading, currentCourseID, i18n?.language],
  );

  // render function
  return (
    <div className={childClassNames}>
      <ErrorBoundary>
        {renderBtn}
        <ModalChangePresence
          loading={courseLoading}
          isOpen={isOpenAlert}
          setOpen={setIsOpenAlert}
          okHandle={alertOk}
          cancelHandle={alertCancel}
        />
        {renderModal}
      </ErrorBoundary>
    </div>
  );
}

AttendanceControl.defaultProps = {
  mode: "attendance",
  dateFormat: "DD.MM.YYYY",
};
AttendanceControl.propTypes = {
  dispatch: PropTypes.func.isRequired,
  flushState: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  loadingCnahge: PropTypes.bool,
  loadAttendance: PropTypes.func.isRequired,
  changeAttendance: PropTypes.func.isRequired,
  // props
  course: PropTypes.oneOfType([
    PropTypes.object.isRequired,
    PropTypes.bool.isRequired,
  ]),
  onDate: PropTypes.oneOfType([
    PropTypes.object.isRequired,
    PropTypes.string.isRequired,
    PropTypes.bool.isRequired,
  ]),
  mode: PropTypes.oneOf(["course", "attendance"]),
};

const mapStateToProps = createStructuredSelector({
  loading: selectAttendanceLoading(),
  loadingCnahge: selectAttendanceLoadingChange(),
  error: selectAttendanceError(),
  attendanceData: makeSelectAttendanceData(),
  courseLoading: makeSelectCourseLoad(),
  courseDetail: makeSelectCourseDetail(),
  onDateAbsences: makeSelectOnDateAbsences(),
  onDateAttendances: makeSelectOnDateAttendances(),
});

function mapDispatchToProps(dispatch) {
  return {
    dispatch,
    flushState: () => dispatch(flushStateAttendanceControl()),
    loadAttendance: id => dispatch(loadAttendance(id)),
    loadCourse: (courseID, onDate, timeZone) => dispatch(loadCourse(courseID, onDate, timeZone)),
    changeAttendance: (attendanceData, timeZone) =>
      dispatch(changeAttendance(attendanceData, timeZone)),
  };
}

const withConnect = connect(
  mapStateToProps,
  mapDispatchToProps,
);

export default compose(
  withConnect,
  memo,
)(AttendanceControl);
